/**
 * The Patient Access manager's daily report — pure derivation, no FHIR I/O.
 *
 * Everything here is computed from resources the caller already fetched, so the
 * arithmetic is unit-testable and the page stays a renderer. The figures are
 * built to reconcile with each other by construction: hour buckets sum to the
 * hero totals, and conversations is the single consult-conversion denominator.
 * A dashboard whose tiles contradict its table teaches the team not to trust
 * either.
 *
 * None of this is FHIR-searchable — pickup, duration and routing all live in
 * base64 JSON inside Communication extensions — which is why the report is
 * assembled client-side from a day's worth of resources rather than aggregated
 * by the server.
 */

import { Appointment, Attachment, Communication, Schedule } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { isCiIntroCallAppointment, PacIvrBranch } from './patient-access';
import { PacVisitType, isCiIntroCallDraft, isCiIntroCallNote, parseCiIntroCallAnswers } from './patient-access-note';
import {
  getExternalNumberFromPhoneCommunication,
  getProviderRoutingHistory,
  isForwardedCommunication,
  isIncomingCallCommunication,
} from './communication';
import { TWILIO_SYSTEM, VIRTUAL_RECEPTIONIST_PAYLOAD_ID } from './constants';
import { SCHEDULING_PARAMETERS_EXTENSION, TWILIO_CALL_HISTORY_EXTENSION } from './extensions';
import { isHoursSetOpen, IvrHoursSet } from './ivr';
import { extractMeetingIdFromJoinUrl, extractVirtualServiceUrlFromAppointment } from './scheduling';
import {
  deriveVoiceCallOutcome,
  getCallDurationSeconds,
  getLastIvrSelection,
  getVoiceRecordingPayloadAttachments,
  VoiceCallOutcome,
} from './voice-call-outcome';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * A count against what it is a count *of*.
 *
 * Every figure on this report is a ratio rather than a bare number: 22 calls
 * picked up is good against 35 routed and poor against 60. Keeping both parts
 * together also makes the zero-denominator case representable instead of
 * surfacing as `NaN%` in the UI.
 */
export interface Ratio {
  numerator: number;
  denominator: number;
}

/** A whole percent, or undefined when there is nothing to divide by. */
export function ratioPercent(ratio: Ratio): number | undefined {
  if (ratio.denominator === 0) {
    return undefined;
  }
  return Math.round((ratio.numerator / ratio.denominator) * 100);
}

/**
 * How an inbound call ended, at the granularity the report counts.
 *
 * `forwarded` is this system's answer to the design's "voicemail" column: there
 * is no mailbox anywhere in the IVR, so a call nobody picks up is dialled out of
 * the building instead — to the answering service for a known patient, to the
 * sales line for an unknown caller. It is deliberately not folded into `missed`:
 * a call a human elsewhere picked up is a callback owed, a missed call is a lost
 * contact, and the staffing response to each is different.
 */
export type PacCallDisposition = 'picked-up' | 'missed' | 'forwarded' | 'in-progress';

export interface PacCoordinator {
  /** `Practitioner/<id>`. */
  reference: string;
  name: string;
}

/** One row of the by-coordinator table. Every figure is a ratio, never a bare count. */
export interface PacCoordinatorRow {
  reference: string;
  name: string;
  /** Picked up against every call routed to them. */
  pickedUp: Ratio;
  /** Their scheduled intro calls: shown against booked. */
  introCalls: Ratio;
  /** Consults booked against this coordinator's own conversations. */
  consults: Ratio;
  /** Seconds spent on answered calls — the numerator for occupancy (see MD-5). */
  talkTimeSeconds: number;
  /**
   * Talk time against the rostered window for the day.
   *
   * **Absent, not zero, when no window covers the day.** An unknown denominator
   * must not render as an empty bar: "we don't know their shift" and "they sat
   * idle all shift" are opposite readings of the same coordinator.
   */
  occupancy?: Ratio;
  /**
   * They are on a call right now — a call in the day's data that has not
   * finished. Derived from the report rather than from presence, so it is true
   * of whatever day is being viewed and needs no live subscription.
   */
  hasLiveCall: boolean;
}

/**
 * The foot of the by-coordinator table.
 *
 * Deliberately **not** the sum of the rows. A call offered to three coordinators
 * before one answered contributes 3 to the routed column and 1 to the team's
 * inbound volume, so summing the column would invent calls that never came in.
 * These are the hero figures.
 */
export interface PacTeamTotals {
  pickedUp: Ratio;
  introCalls: Ratio;
  consults: Ratio;
  talkTimeSeconds: number;
  /**
   * Team talk time against every *known* rostered window. Coordinators with no
   * window contribute to neither side, so the figure stays readable when the
   * roster is only partly scheduled. Absent when nobody has a window at all.
   */
  occupancy?: Ratio;
}

/**
 * One row of the call log.
 *
 * Built from the same classification the hour counts are, so a row can never
 * disagree with the header above it.
 */
export interface PacCallLogEntry {
  /** `Communication/<id>` — the row key. */
  reference: string;
  /** When the call came in. */
  sent: string;
  disposition: PacCallDisposition;
  /** The caller's number as Twilio reported it. Absent when the caller withheld it. */
  callerNumber?: string;
  /** `Patient/<id>` when the number matched a patient; absent for an unknown number. */
  patientReference?: string;
  /**
   * `Practitioner/<id>` who answered. Absent unless the call was picked up.
   *
   * A ringing call carries the coordinator it is *currently* offered to in
   * `recipient`, and forwarding clears it. So the reference is only an answerer
   * on a picked-up call; anywhere else it would name someone who let it ring.
   */
  answeredBy?: string;
  /** Zero while the call is still in progress — render it as live, not as 0:00. */
  durationSeconds: number;
  /** Recording segments, in the order Twilio produced them. */
  recordings: Attachment[];
  /**
   * The answering service posted a message back for this call.
   *
   * Only ever true on a `forwarded` row, and only once AnswerConnect has sent
   * it, which is minutes to hours after the call. A forwarded row without it is
   * not yet evidence that no message was taken.
   */
  messageTaken?: boolean;
}

/**
 * One scheduled intro call, as a row in the hour it starts in.
 *
 * A booked Zoom consult is a different kind of event from a call off the phone
 * queue, so it never joins `calls` and never touches the pickup rate: the caller
 * chose the time, and nobody was offered the chance to miss it.
 */
export interface PacIntroCallLogEntry {
  /** `Appointment/<id>` — the row key. */
  reference: string;
  start: string;
  /**
   * `booked` is a call that has neither happened nor been written off yet, which
   * is the normal state for later today.
   */
  status: 'shown' | 'no-show' | 'booked';
  /** `Patient/<id>`, when a patient is on the appointment. */
  patientReference?: string;
  /** `Practitioner/<id>` holding the slot. */
  practitionerReference?: string;
  /** The Zoom join URL, when the appointment carries one. */
  joinUrl?: string;
  /** Numeric Zoom meeting id, parsed out of the join URL. */
  meetingId?: string;
  /** Scheduled length. Absent when the appointment has no end. */
  durationMinutes?: number;
}

export interface PacHourBucket {
  /** Hour of the day, 0-23, in the report timezone. */
  hour: number;
  inbound: number;
  pickedUp: number;
  missed: number;
  /** Forwarded out of the building. Counted apart from `missed`, never inside it. */
  forwarded: number;
  inProgress: number;
  /** Picked up against every inbound call in the hour, in progress included. */
  pickupRate: Ratio;
  /** The hour's calls, earliest first. Sums to `inbound` by construction. */
  calls: PacCallLogEntry[];
  /**
   * Scheduled intro calls starting in this hour. Counted separately from
   * `inbound` so they never dilute the pickup rate — a booked Zoom call was
   * never offered to the phone queue.
   */
  scheduledIntroCalls: number;
  scheduledIntroCallsShown: number;
  /** The hour's intro calls, earliest first. Sums to `scheduledIntroCalls`. */
  introCalls: PacIntroCallLogEntry[];
}

export interface PacDailyReport {
  /**
   * False when no intake phone-tree branch is configured, in which case every
   * call figure below is zero because nothing was counted — not because the
   * line was quiet. The page has to say which of those it is.
   */
  intakeBranchConfigured: boolean;
  /**
   * Callers who heard the menu and chose nothing.
   *
   * Not intake's calls — nobody asked for intake — so they stay out of every
   * ratio above. Surfaced because they are the largest population on the line
   * by far: in production two thirds of inbound calls end this way, and a
   * manager cannot act on a number nobody shows them.
   */
  noSelection: {
    total: number;
    /** Handed on to the answering service or the sales line. */
    forwarded: number;
    /** Hung up during the menu. Nobody spoke to them at all. */
    abandoned: number;
  };
  inboundCalls: { businessHours: number; afterHours: number; total: number };
  pickup: { businessHours: Ratio; allHours: Ratio };
  scheduledIntroCalls: { booked: number; shown: number; noShows: number };
  /**
   * Picked-up inbounds answered by a coordinator, plus scheduled intro calls that
   * showed. The single denominator for consult conversion: missed calls and
   * no-shows are excluded, because including them turns a staffing problem into a
   * performance number.
   */
  conversations: number;
  consults: {
    /** Consults booked against all conversations. */
    total: Ratio;
    /** Conversion on inbound calls, which converts far worse than a booked call. */
    fromInbound: Ratio;
    /** Conversion on scheduled intro calls — the higher-yield motion. */
    fromScheduled: Ratio;
  };
  /** One row per rostered coordinator, in roster order. */
  coordinators: PacCoordinatorRow[];
  teamTotal: PacTeamTotals;
  /** Ascending by hour; hours with no activity are absent rather than zeroed. */
  hours: PacHourBucket[];
}

export interface PacDailyReportInput {
  /** The day's Communications. Non-inbound-call entries are ignored. */
  calls: Communication[];
  /**
   * The phone-tree branches that reach Care Intake — "press 2".
   *
   * **Required, and an empty list means no calls.** This report is about
   * intake, not the switchboard: unscoped it counted every inbound call to
   * every number and presented the lot as CI's pickup rate. Every Bariendo
   * number funnels into the same menu, no routing action targets the CI role,
   * and the intake hours set carries no Location, so the digit pressed is the
   * only thing that identifies an intake call.
   *
   * Empty deliberately yields an empty report rather than falling back to every
   * call: a dashboard that looks like it is working while reporting the wrong
   * population is worse than one that says it is not configured.
   */
  intakeIvrBranches: PacIvrBranch[];
  introCallAppointments: Appointment[];
  introCallNotes: Communication[];
  coordinators: PacCoordinator[];
  /** Business hours; everything outside them is after hours. */
  hoursSet: IvrHoursSet;
  /** IANA zone the report is expressed in — PT for this dashboard. */
  timezone: string;
  /**
   * The calendar day the report covers, `YYYY-MM-DD` in `timezone`. Only the
   * weekday is used, to pick the right rostered window out of a weekly schedule.
   */
  date?: string;
  /**
   * Appointment-availability `Schedule`s keyed by coordinator (`Practitioner/<id>`).
   *
   * The join lives in the caller because `Schedule.actor` is a `PractitionerRole`
   * and this module has no FHIR access to resolve one. Omitted entirely, every
   * occupancy is absent — which is the honest rendering when nobody's shift is
   * recorded.
   */
  schedulesByCoordinator?: Record<string, Schedule[]>;
}

/**
 * An unresolved outcome means the call has not finished yet.
 *
 * `deriveVoiceCallOutcome` returns undefined for `preparation` and `in-progress`,
 * which is exactly the live-call case the current hour needs to show. Treating it
 * as missed would punish a coordinator mid-conversation.
 *
 * Exported so the call log renders rows from the same classification the counts
 * are built from, rather than re-deriving it and drifting.
 */
export function getCiCallDisposition(call: Communication): PacCallDisposition {
  const outcome = deriveVoiceCallOutcome(call);
  if (!outcome) {
    return 'in-progress';
  }
  if (outcome === VoiceCallOutcome.ANSWERED) {
    return 'picked-up';
  }
  return outcome === VoiceCallOutcome.INCOMING_FORWARDED ? 'forwarded' : 'missed';
}

/** FHIR `daysOfWeek` codes, indexed the way `dayjs().day()` numbers them. */
const FHIR_DAYS_OF_WEEK = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const SECONDS_PER_UNIT: Record<string, number> = { s: 1, min: 60, h: 3600, d: 86400 };

interface ScheduledWindow {
  startSeconds: number;
  endSeconds: number;
}

/**
 * Rostered windows on `weekday`, read off one Schedule's scheduling parameters.
 *
 * A window needs a `timeOfDay` to be positioned in the day. One without a start
 * is skipped rather than counted at an arbitrary offset — it cannot be
 * reconciled against the others, and everything `set-weekly-availability`
 * writes has one.
 */
function scheduleWindowsForWeekday(schedule: Schedule, weekday: string): ScheduledWindow[] {
  const windows: ScheduledWindow[] = [];

  for (const parameters of schedule.extension ?? []) {
    if (parameters.url !== SCHEDULING_PARAMETERS_EXTENSION) {
      continue;
    }

    const repeat = parameters.extension?.find((entry) => entry.url === 'availability')?.valueTiming?.repeat;
    if (!repeat?.dayOfWeek?.includes(weekday as 'mon')) {
      continue;
    }

    const unitSeconds = SECONDS_PER_UNIT[repeat.durationUnit ?? ''];
    const durationSeconds = (repeat.duration ?? 0) * (unitSeconds ?? 0);
    if (durationSeconds <= 0) {
      continue;
    }

    for (const timeOfDay of repeat.timeOfDay ?? []) {
      const [hours, minutes, seconds] = timeOfDay.split(':').map(Number);
      if (!Number.isFinite(hours)) {
        continue;
      }
      const startSeconds = hours * 3600 + (minutes || 0) * 60 + (seconds || 0);
      windows.push({ startSeconds, endSeconds: startSeconds + durationSeconds });
    }
  }

  return windows;
}

/**
 * How long the coordinator was rostered for on `date`, in seconds.
 *
 * Overlapping windows are **merged, not summed**. A coordinator carrying two
 * availability entries for the same 08:00 block — one per service type, which
 * is how these records are actually written — works one nine-hour shift, not
 * two. Merging also handles a genuine split shift correctly: 08:00–12:00 plus
 * 13:00–17:00 is eight hours, with the lunch hour excluded rather than counted.
 *
 * Returns undefined when nothing covers that weekday, which is the signal to
 * render occupancy as unknown instead of zero.
 */
export function getScheduledSecondsForDay(
  schedules: Schedule[],
  date: string,
  reportTimezone: string
): number | undefined {
  const weekday = FHIR_DAYS_OF_WEEK[dayjs.tz(date, reportTimezone).day()];

  const windows = schedules
    .flatMap((schedule) => scheduleWindowsForWeekday(schedule, weekday))
    .sort((left, right) => left.startSeconds - right.startSeconds);

  if (windows.length === 0) {
    return undefined;
  }

  let total = 0;
  let mergedStart = windows[0].startSeconds;
  let mergedEnd = windows[0].endSeconds;

  for (const window of windows.slice(1)) {
    if (window.startSeconds > mergedEnd) {
      total += mergedEnd - mergedStart;
      mergedStart = window.startSeconds;
      mergedEnd = window.endSeconds;
    } else {
      mergedEnd = Math.max(mergedEnd, window.endSeconds);
    }
  }

  return total + (mergedEnd - mergedStart);
}

/**
 * Has the answering service sent back what the caller wanted?
 *
 * `registerVirtualReceptionistData` appends the AnswerConnect message to the
 * call it belongs to as a `virtual-receptionist` payload, so its presence is the
 * only "a message is waiting" signal there is.
 */
function hasAnsweringServiceMessage(call: Communication): boolean {
  return (call.payload ?? []).some((payload) => payload.id === VIRTUAL_RECEPTIONIST_PAYLOAD_ID);
}

interface ClassifiedCall {
  disposition: PacCallDisposition;
  duringBusinessHours: boolean;
  hour: number;
  /** Whoever the call last reached — the answerer on a picked-up call. */
  answeredBy?: string;
  /** Everyone the call was offered to, answerer included. */
  routedTo: string[];
  durationSeconds: number;
  /** The call-log row, carried alongside the counts so the two cannot diverge. */
  entry: PacCallLogEntry;
}

/** The Twilio CallSid the call was registered under — its identity, not the Communication's. */
function parentCallSid(call: Communication): string | undefined {
  return call.identifier?.find((identifier) => identifier.system === TWILIO_SYSTEM)?.value;
}

/** How much of the Twilio conversation this Communication actually witnessed. */
function recordedHistoryLength(call: Communication): number {
  return (call.extension?.find((extension) => extension.url === TWILIO_CALL_HISTORY_EXTENSION)?.extension ?? []).length;
}

/**
 * One row per call, not per Communication.
 *
 * A single inbound call becomes several Communications two different ways, and
 * counting rows counted the call once per Communication both times:
 *
 * 1. **By design** — one per Patient whose number matched the caller, so the
 *    call lands on each of their timelines. A family sharing a phone, or
 *    duplicate patient records, multiplies a call by however many matched.
 * 2. **By defect** — the voice webhook could be re-entered mid-call and
 *    registration was an unconditional create, so a call could be written two to
 *    six times over. Fixed in the bots, but every call already stored keeps its
 *    duplicates, and this report has to read that history.
 *
 * Where duplicates disagree, the fullest record wins: one minted seconds before
 * the call ended can hold nothing but the opening webhook, and picking it would
 * show a finished call as still in progress, with no answerer and no recording.
 * A Communication with no Twilio identifier stands alone rather than being
 * grouped under a shared blank key.
 */
function dedupeByCall(calls: Communication[]): Communication[] {
  const byCall = new Map<string, Communication>();

  for (const [index, call] of calls.entries()) {
    const key = parentCallSid(call) ?? `communication:${call.id ?? index}`;
    const incumbent = byCall.get(key);

    if (
      !incumbent ||
      recordedHistoryLength(call) > recordedHistoryLength(incumbent) ||
      (recordedHistoryLength(call) === recordedHistoryLength(incumbent) && (call.sent ?? '') < (incumbent.sent ?? ''))
    ) {
      byCall.set(key, call);
    }
  }

  return [...byCall.values()];
}

/** Did this caller press their way to Care Intake? */
function isIntakeCall(call: Communication, branches: PacIvrBranch[]): boolean {
  // The last press is the one that decided where the call went.
  const selection = getLastIvrSelection(call);
  return branches.some((branch) => branch.templateId === selection?.templateId && branch.digit === selection?.digit);
}

/** Every inbound call on the line, intake's or not, deduped to one row per call. */
function inboundCalls(input: PacDailyReportInput): Communication[] {
  return dedupeByCall(input.calls.filter(isIncomingCallCommunication));
}

function classifyCalls(input: PacDailyReportInput): ClassifiedCall[] {
  return inboundCalls(input)
    .filter((call) => isIntakeCall(call, input.intakeIvrBranches))
    .map((call) => {
      const sent = dayjs(call.sent);
      const disposition = getCiCallDisposition(call);
      const answeredBy = call.recipient?.[0]?.reference;
      const durationSeconds = getCallDurationSeconds(call);
      const callerNumber = getExternalNumberFromPhoneCommunication(call);
      // An unknown number has no Patient subject at all, which is exactly the case
      // the log has to render as a bare number rather than a blank row.
      const patientReference = call.subject?.reference?.startsWith('Patient/') ? call.subject.reference : undefined;

      return {
        disposition,
        duringBusinessHours: isHoursSetOpen(input.hoursSet, sent),
        hour: sent.tz(input.timezone).hour(),
        ...(answeredBy ? { answeredBy } : {}),
        routedTo: getProviderRoutingHistory(call)
          .map((reference) => reference.reference)
          .filter((reference): reference is string => Boolean(reference)),
        durationSeconds,
        entry: {
          reference: call.id ? `Communication/${call.id}` : (call.sent ?? ''),
          sent: call.sent ?? '',
          disposition,
          ...(callerNumber ? { callerNumber } : {}),
          ...(patientReference ? { patientReference } : {}),
          // Only on a picked-up row: `recipient` is whoever the call is ringing at
          // the time, so on a missed row it would credit the coordinator who let
          // it ring with having taken it.
          ...(answeredBy && disposition === 'picked-up' ? { answeredBy } : {}),
          durationSeconds,
          recordings: getVoiceRecordingPayloadAttachments(call),
          ...(hasAnsweringServiceMessage(call) ? { messageTaken: true } : {}),
        },
      };
    });
}

/**
 * Statuses that mean the call happened. `booked` and `pending` are neither shown
 * nor a no-show yet, which is why shown + noShows can be under booked on a day
 * still in progress.
 */
const SHOWN_APPOINTMENT_STATUSES: ReadonlySet<string> = new Set(['fulfilled', 'arrived', 'checked-in']);

/** Never counted as booked at all — a cancelled call was not a commitment kept or broken. */
const UNBOOKED_APPOINTMENT_STATUSES: ReadonlySet<string> = new Set(['cancelled', 'entered-in-error']);

interface ClassifiedIntroCall {
  shown: boolean;
  noShow: boolean;
  practitioner?: string;
  hour: number;
  /** The log row, carried alongside the counts so the two cannot diverge. */
  entry: PacIntroCallLogEntry;
}

/** Scheduled length in whole minutes, or absent when the appointment has no end. */
function appointmentDurationMinutes(appointment: Appointment): number | undefined {
  if (!appointment.start || !appointment.end) {
    return undefined;
  }
  const minutes = dayjs(appointment.end).diff(dayjs(appointment.start), 'minute');
  return minutes > 0 ? minutes : undefined;
}

function classifyIntroCalls(input: PacDailyReportInput): ClassifiedIntroCall[] {
  return input.introCallAppointments
    .filter(isCiIntroCallAppointment)
    .filter((appointment) => !UNBOOKED_APPOINTMENT_STATUSES.has(appointment.status ?? ''))
    .map((appointment) => {
      const practitioner = appointment.participant?.find((participant) =>
        participant.actor?.reference?.startsWith('Practitioner/')
      )?.actor?.reference;
      const patient = appointment.participant?.find((participant) =>
        participant.actor?.reference?.startsWith('Patient/')
      )?.actor?.reference;

      const shown = SHOWN_APPOINTMENT_STATUSES.has(appointment.status ?? '');
      const noShow = appointment.status === 'noshow';
      const joinUrl = extractVirtualServiceUrlFromAppointment(appointment);
      const meetingId = joinUrl ? extractMeetingIdFromJoinUrl(joinUrl) : undefined;
      const durationMinutes = appointmentDurationMinutes(appointment);

      return {
        shown,
        noShow,
        ...(practitioner ? { practitioner } : {}),
        hour: dayjs(appointment.start).tz(input.timezone).hour(),
        entry: {
          reference: appointment.id ? `Appointment/${appointment.id}` : (appointment.start ?? ''),
          start: appointment.start ?? '',
          status: shown ? 'shown' : noShow ? 'no-show' : 'booked',
          ...(patient ? { patientReference: patient } : {}),
          ...(practitioner ? { practitionerReference: practitioner } : {}),
          ...(joinUrl ? { joinUrl } : {}),
          ...(meetingId ? { meetingId } : {}),
          ...(durationMinutes ? { durationMinutes } : {}),
        },
      };
    });
}

interface ClassifiedIntroCallNote {
  sender?: string;
  visitType: PacVisitType;
  /** The rep booked the consult on this call. */
  bookedConsult: boolean;
}

/**
 * Only a charted note counts as a booked consult.
 *
 * While the note is a draft its slot is merely *held* for the duration of the
 * call, and is released rather than booked if the call closes without one.
 * Counting a draft would report consults that never existed.
 */
function classifyIntroCallNotes(input: PacDailyReportInput): ClassifiedIntroCallNote[] {
  return input.introCallNotes
    .filter(isCiIntroCallNote)
    .filter((note) => !isCiIntroCallDraft(note))
    .map((note) => {
      const answers = parseCiIntroCallAnswers(note);
      return {
        ...(note.sender?.reference ? { sender: note.sender.reference } : {}),
        visitType: answers.visitType,
        bookedConsult: Boolean(answers.slot),
      };
    });
}

/**
 * Callers who heard the menu and pressed nothing.
 *
 * Counted over the whole line rather than intake's slice, because the point of
 * the figure is the traffic that reached no team at all.
 */
function countNoSelection(input: PacDailyReportInput): PacDailyReport['noSelection'] {
  const silent = inboundCalls(input).filter((call) => !getLastIvrSelection(call));
  const forwarded = silent.filter(isForwardedCommunication).length;

  return { total: silent.length, forwarded, abandoned: silent.length - forwarded };
}

function pickupRatio(calls: ClassifiedCall[]): Ratio {
  return {
    numerator: calls.filter((call) => call.disposition === 'picked-up').length,
    denominator: calls.length,
  };
}

function buildHourBuckets(calls: ClassifiedCall[], introCalls: ClassifiedIntroCall[]): PacHourBucket[] {
  // An hour appears if anything happened in it, from either source — a booked
  // Zoom call with no phone traffic still deserves a row.
  const hours = [...new Set([...calls.map((call) => call.hour), ...introCalls.map((introCall) => introCall.hour)])];

  return hours
    .sort((left, right) => left - right)
    .map((hour) => {
      const hourCalls = calls.filter((call) => call.hour === hour);
      const hourIntroCalls = introCalls.filter((introCall) => introCall.hour === hour);

      return {
        hour,
        inbound: hourCalls.length,
        pickedUp: hourCalls.filter((call) => call.disposition === 'picked-up').length,
        missed: hourCalls.filter((call) => call.disposition === 'missed').length,
        forwarded: hourCalls.filter((call) => call.disposition === 'forwarded').length,
        inProgress: hourCalls.filter((call) => call.disposition === 'in-progress').length,
        pickupRate: pickupRatio(hourCalls),
        calls: hourCalls.map((call) => call.entry).sort((left, right) => left.sent.localeCompare(right.sent)),
        scheduledIntroCalls: hourIntroCalls.length,
        scheduledIntroCallsShown: hourIntroCalls.filter((introCall) => introCall.shown).length,
        introCalls: hourIntroCalls
          .map((introCall) => introCall.entry)
          .sort((left, right) => left.start.localeCompare(right.start)),
      };
    });
}

function buildCoordinatorRow(
  coordinator: PacCoordinator,
  calls: ClassifiedCall[],
  introCalls: ClassifiedIntroCall[],
  notes: ClassifiedIntroCallNote[],
  scheduledSeconds: number | undefined
): PacCoordinatorRow {
  const routedToThem = calls.filter((call) => call.routedTo.includes(coordinator.reference));
  const answeredByThem = calls.filter(
    (call) => call.disposition === 'picked-up' && call.answeredBy === coordinator.reference
  );

  const theirIntroCalls = introCalls.filter((introCall) => introCall.practitioner === coordinator.reference);
  const theirShownIntroCalls = theirIntroCalls.filter((introCall) => introCall.shown);
  const theirConsults = notes.filter((note) => note.sender === coordinator.reference && note.bookedConsult);
  const talkTimeSeconds = answeredByThem.reduce((total, call) => total + call.durationSeconds, 0);

  return {
    reference: coordinator.reference,
    name: coordinator.name,
    pickedUp: { numerator: answeredByThem.length, denominator: routedToThem.length },
    introCalls: { numerator: theirShownIntroCalls.length, denominator: theirIntroCalls.length },
    consults: {
      numerator: theirConsults.length,
      denominator: answeredByThem.length + theirShownIntroCalls.length,
    },
    talkTimeSeconds,
    ...(scheduledSeconds === undefined
      ? {}
      : { occupancy: { numerator: talkTimeSeconds, denominator: scheduledSeconds } }),
    hasLiveCall: calls.some(
      (call) => call.disposition === 'in-progress' && call.routedTo.includes(coordinator.reference)
    ),
  };
}

export function buildCiDailyReport(input: PacDailyReportInput): PacDailyReport {
  const calls = classifyCalls(input);
  const businessHoursCalls = calls.filter((call) => call.duringBusinessHours);

  const roster = new Set(input.coordinators.map((coordinator) => coordinator.reference));
  const introCalls = classifyIntroCalls(input);
  const notes = classifyIntroCallNotes(input);

  // A conversation is a call a coordinator actually had. Pickup rate measures the
  // phone line; this measures the team, so a call the on-call nurse answered is
  // excluded even though it was picked up.
  const pacPickedUpCalls = calls.filter(
    (call) => call.disposition === 'picked-up' && call.answeredBy && roster.has(call.answeredBy)
  );
  const shownIntroCalls = introCalls.filter((introCall) => introCall.shown);
  const conversations = pacPickedUpCalls.length + shownIntroCalls.length;

  const consultsFromInbound = notes.filter((note) => note.visitType === 'inbound' && note.bookedConsult).length;
  const consultsFromScheduled = notes.filter((note) => note.visitType === 'zoom' && note.bookedConsult).length;
  const consultsTotal = consultsFromInbound + consultsFromScheduled;

  const coordinatorRows = input.coordinators.map((coordinator) => {
    const schedules = input.schedulesByCoordinator?.[coordinator.reference];
    const scheduledSeconds =
      input.date && schedules?.length ? getScheduledSecondsForDay(schedules, input.date, input.timezone) : undefined;

    return buildCoordinatorRow(coordinator, calls, introCalls, notes, scheduledSeconds);
  });

  const scheduledRows = coordinatorRows.filter((row) => row.occupancy);

  return {
    intakeBranchConfigured: input.intakeIvrBranches.length > 0,
    noSelection: countNoSelection(input),
    inboundCalls: {
      businessHours: businessHoursCalls.length,
      afterHours: calls.length - businessHoursCalls.length,
      total: calls.length,
    },
    pickup: {
      businessHours: pickupRatio(businessHoursCalls),
      allHours: pickupRatio(calls),
    },
    scheduledIntroCalls: {
      booked: introCalls.length,
      shown: shownIntroCalls.length,
      noShows: introCalls.filter((introCall) => introCall.noShow).length,
    },
    conversations,
    consults: {
      total: { numerator: consultsTotal, denominator: conversations },
      fromInbound: { numerator: consultsFromInbound, denominator: pacPickedUpCalls.length },
      fromScheduled: { numerator: consultsFromScheduled, denominator: shownIntroCalls.length },
    },
    coordinators: coordinatorRows,
    teamTotal: {
      pickedUp: pickupRatio(calls),
      introCalls: { numerator: shownIntroCalls.length, denominator: introCalls.length },
      consults: { numerator: consultsTotal, denominator: conversations },
      // Summed over the rows, not over every call: talk time is a per-person
      // quantity, so a call answered outside the PAC roster belongs to no row and
      // must not appear in the total either. The ratios above are different —
      // they describe the whole phone line, so they stay the hero figures.
      talkTimeSeconds: coordinatorRows.reduce((total, row) => total + row.talkTimeSeconds, 0),
      ...(scheduledRows.length === 0
        ? {}
        : {
            occupancy: {
              // Only the scheduled rows on both sides. Adding an unrostered
              // coordinator's talk time to a denominator they contributed
              // nothing to would push team occupancy above what anyone worked.
              numerator: scheduledRows.reduce((total, row) => total + row.talkTimeSeconds, 0),
              denominator: scheduledRows.reduce((total, row) => total + (row.occupancy?.denominator ?? 0), 0),
            },
          }),
    },
    hours: buildHourBuckets(calls, introCalls),
  };
}
